import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User, UserRole } from '@/types/models';
import toast from 'react-hot-toast';
import { authService } from '@/services/api/auth.service';
import { getAccessToken, clearAuthTokens } from '@/config/auth';

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  mustChangePassword: boolean;

  // Actions
  login: (email: string, password: string, rememberMe?: boolean) => Promise<User>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  setUser: (user: User | null) => void;
}

// Helper functions
export const isAuthenticated = (state: AuthState) => state.isAuthenticated;
export const isAdmin = (state: AuthState) => state.user?.role === 'Administrador';
export const isDoctor = (state: AuthState) => state.user?.role === 'Médico';
export const isReceptionist = (state: AuthState) => state.user?.role === 'Recepcionista';
export const hasRole = (state: AuthState, role: UserRole) => state.user?.role === role;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      mustChangePassword: false,

      // Login action
      login: async (email: string, password: string, rememberMe = false) => {
        console.log('[Auth Store] Login iniciado para:', email);
        set({ isLoading: true });
        try {
          // Call login API (stores tokens internally)
          const response = await authService.login({ email, password, rememberMe });

          console.log('[Auth Store] Login bem-sucedido, user:', response.user.email);

          set({
            user: {
              id: response.user.id,
              email: response.user.email,
              name: response.user.name,
              isActive: response.user.isActive,
              mustChangePassword: response.user.mustChangePassword,
              role: response.user.role as UserRole,
              // LoginResponse returns clinicIds but User expects clinics with full objects
              // We'll fetch clinic details separately if needed
              clinics: undefined,
              doctor: undefined,
            },
            isAuthenticated: true,
            mustChangePassword: response.user.mustChangePassword || false,
            isLoading: false,
            isInitialized: true,
          });

          toast.success('Login realizado com sucesso!');
          
          return response.user as User;
        } catch (error: any) {
          console.error('[Auth Store] Erro no login:', error);
          set({ isLoading: false, isAuthenticated: false });

          // Handle API errors
          const message = error.response?.data?.error || error.message || 'Erro ao fazer login';

          if (message.includes('Invalid email or password')) {
            toast.error('Email ou senha incorretos');
          } else if (message.includes('Account is inactive')) {
            toast.error('Conta inativa. Entre em contato com o administrador');
          } else {
            toast.error(message);
          }

          throw error;
        }
      },

      // Logout action
      logout: async () => {
        try {
          await authService.logout();
          set({
            user: null,
            isAuthenticated: false,
            mustChangePassword: false,
          });
          toast.success('Logout realizado com sucesso');
        } catch (error: any) {
          console.error('[Auth Store] Erro no logout:', error);
          // Always clear local state even if API call fails
          set({
            user: null,
            isAuthenticated: false,
            mustChangePassword: false,
          });
          toast.error('Erro ao fazer logout');
        }
      },

      // Check authentication on app load
      checkAuth: async () => {
        const token = getAccessToken();

        if (!token) {
          set({ isInitialized: true, isAuthenticated: false });
          return;
        }

        try {
          console.log('[Auth Store] Verificando autenticação...');
          const data = await authService.validateUser();

          if (data.valid && data.user) {
            console.log('[Auth Store] Usuário autenticado:', data.user.email);
            set({
              user: {
                id: data.user.id,
                email: data.user.email,
                name: data.user.name,
                isActive: data.user.isActive,
                mustChangePassword: data.user.mustChangePassword,
                role: data.user.role as UserRole,
                clinics: data.user.clinics,
                doctor: data.user.doctor,
              },
              isAuthenticated: true,
              mustChangePassword: data.user.mustChangePassword || false,
              isInitialized: true,
            });
          } else {
            // Invalid token, clear auth
            clearAuthTokens();
            set({
              user: null,
              isAuthenticated: false,
              isInitialized: true
            });
          }
        } catch (error) {
          console.error('[Auth Store] Erro ao validar token:', error);
          // Token validation failed, clear auth and initialize
          clearAuthTokens();
          set({
            user: null,
            isAuthenticated: false,
            isInitialized: true,
          });
        }
      },

      // Refresh authentication (called when token expires)
      refreshAuth: async () => {
        try {
          console.log('[Auth Store] Tentando refresh do token...');
          await authService.refreshAccessToken();

          // Token refreshed successfully, validate the new token
          const data = await authService.validateUser();

          if (data.valid && data.user) {
            console.log('[Auth Store] Token refresh bem-sucedido:', data.user.email);
            set({
              user: {
                id: data.user.id,
                email: data.user.email,
                name: data.user.name,
                isActive: data.user.isActive,
                mustChangePassword: data.user.mustChangePassword,
                role: data.user.role as UserRole,
                clinics: data.user.clinics,
                doctor: data.user.doctor,
              },
              isAuthenticated: true,
              mustChangePassword: data.user.mustChangePassword || false,
              isInitialized: true,
            });
          } else {
            // Refresh succeeded but validation failed, clear auth
            clearAuthTokens();
            set({
              user: null,
              isAuthenticated: false,
              isInitialized: true,
            });
          }
        } catch (error) {
          console.error('[Auth Store] Erro ao fazer refresh:', error);
          // Refresh failed, clear auth
          clearAuthTokens();
          set({
            user: null,
            isAuthenticated: false,
            isInitialized: true,
          });
        }
      },

      // Change password action
      changePassword: async (oldPassword: string, newPassword: string) => {
        const user = get().user;
        if (!user) throw new Error('Usuário não autenticado');

        set({ isLoading: true });
        try {
          await authService.changePassword(oldPassword, newPassword);
          set({ mustChangePassword: false, isLoading: false });
          toast.success('Senha alterada com sucesso!');
        } catch (error: any) {
          set({ isLoading: false });

          const message = error.response?.data?.error || error.message;

          if (message?.includes('Current password is incorrect')) {
            toast.error('Senha atual incorreta');
          } else if (message?.includes('at least 6 characters')) {
            toast.error('A senha deve ter pelo menos 6 caracteres');
          } else {
            toast.error('Erro ao alterar senha');
          }

          throw error;
        }
      },

      // Set user (for manual updates)
      setUser: (user: User | null) => {
        set({ user, isAuthenticated: !!user });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist user data (tokens are stored separately)
        user: state.user,
        mustChangePassword: state.mustChangePassword,
      }),
    }
  )
);

// Initialize auth on app load
if (typeof window !== 'undefined') {
  useAuthStore.getState().checkAuth();
}
